import { useEffect, useRef, useState } from 'react'

/**
 * A whole-number box that cannot be pasted into nonsense — ROADMAP 9.66.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT MOVED UP BECAUSE A SECOND SCREEN NEEDED THE SAME VALIDATIONS
 *
 * It was private to `SizeControl`, which was right while the header's size
 * box was the only place a trader typed a lot count. The basket builder gives
 * every LEG its own stepper, and the owner's requirement was "the exact same
 * validations and working as our current one" — which is a requirement that
 * there be one of this, not two that agree today.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT ACTUALLY GUARDS
 *
 * NON-DIGITS NEVER REACH THE PARENT. `Number('')` is 0 and `Number('1e9')` is
 * a billion; both would be read as a size.
 *
 * A PASTE IS CLAMPED, NOT TRUNCATED. Truncating "99999999999" to its first
 * digits invents a number nobody typed; clamping to the ceiling is at least
 * the most of what they asked for. It also keeps `Number()` away from
 * Infinity.
 *
 * AN EMPTY BOX IS NOT NECESSARILY ZERO. `emptyValue: null` leaves the parent
 * on its last real value while the field is mid-edit — a half-deleted "2"
 * must not momentarily arm an order at zero lots — and `0` is for the boxes
 * where empty genuinely means none.
 *
 * THE TEXT IS NOT RE-DERIVED WHILE FOCUSED, or the cursor jumps to the end on
 * every keystroke the parent echoes back.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function DigitInput({
  value,
  onValue,
  max,
  /** What an empty box means. `null` tells the parent nothing until a digit. */
  emptyValue,
  label,
  testId,
  placeholder,
  className,
}: {
  value: number
  onValue: (v: number) => void
  max: number
  emptyValue: number | null
  label: string
  testId: string
  placeholder: string
  className: string
}) {
  const show = (v: number): string => (v === 0 && emptyValue === 0 ? '' : String(v))
  const [text, setText] = useState(() => show(value))
  const focused = useRef(false)

  useEffect(() => {
    if (focused.current) return
    setText(show(value))
    // `show` is derived from `emptyValue`, which never changes for a given box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, emptyValue])

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      aria-label={label}
      data-testid={testId}
      placeholder={placeholder}
      value={text}
      onFocus={() => {
        focused.current = true
      }}
      onBlur={() => {
        focused.current = false
        setText(show(value))
      }}
      onChange={(e) => {
        /*
         * Stripped, then CLAMPED rather than truncated. Truncating a paste of
         * "99999999999" to its first digits invents a number nobody typed;
         * clamping to the ceiling is at least a number they asked for the
         * most of. The cap also keeps `Number()` away from Infinity.
         */
        const digits = e.target.value.replace(/\D/g, '').slice(0, 15)
        if (digits === '') {
          setText('')
          if (emptyValue !== null) onValue(emptyValue)
          return
        }
        const next = Math.min(max, Number(digits))
        setText(String(next))
        onValue(next)
      }}
      className={className}
    />
  )
}
